/**
 * Google Health insights for a set of participants over a date range (backend: getHealthInsights).
 * Used by the project's Insights tab (participant picker) and by each participant's page.
 */
import {
  collection, limit, onSnapshot, orderBy, query, where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { db, functions } from '../../firebase';

const DAY = 86400000;
const MAX_DAYS = 400; // the backend's limit per request
const FINISHED = ['done', 'done_with_errors'];
const iso = (ms) => new Date(ms).toISOString().slice(0, 10);
const BLUE = '#0A66C2';
const muted = { fontSize: 13, color: 'var(--jp-gray-600)', margin: 0, lineHeight: 1.5 };

const axis = {
  tick: { fontSize: 11, fontFamily: 'var(--jp-mono)', fill: 'var(--jp-gray-600)' },
  axisLine: { stroke: 'var(--jp-gray-200)' },
  tickLine: false,
};
const grid = { stroke: 'var(--jp-gray-100)', strokeDasharray: '3 3' };
const fmt = (v, d = 0) => (v == null ? '–' : Number(v).toLocaleString(undefined, { maximumFractionDigits: d }));

function Stat({ label, value, sub }) {
  return (
    <div style={{
      background: '#fff', border: '1px solid var(--jp-gray-200)', borderRadius: 'var(--jp-radius)',
      padding: '12px 16px', flex: '1 1 130px',
    }}>
      <div style={{ fontSize: 11, color: 'var(--jp-gray-600)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 500, fontFamily: 'var(--jp-mono)', color: 'var(--jp-gray-800)', marginTop: 4 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--jp-gray-600)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}
Stat.propTypes = { label: PropTypes.string, value: PropTypes.node, sub: PropTypes.string };

function ChartCard({ title, note, children, height = 220 }) {
  return (
    <div className="ja-card">
      <div className="ja-card-header"><h4 className="ja-card-title">{title}</h4></div>
      {note && <p style={{ ...muted, marginBottom: 10 }}>{note}</p>}
      <ResponsiveContainer width="100%" height={height}>{children}</ResponsiveContainer>
    </div>
  );
}
ChartCard.propTypes = { title: PropTypes.string, note: PropTypes.string, children: PropTypes.node, height: PropTypes.number };

/** One square per day, shaded by wear hours (single participant). */
function CoverageCalendar({ daily, startDate, endDate }) {
  const byDay = Object.fromEntries(daily.map((d) => [d.day, d]));
  const cells = [];
  for (let t = Date.parse(startDate); t <= Date.parse(endDate); t += DAY) {
    const day = iso(t);
    const d = byDay[day];
    const h = d?.wearHours || 0;
    const bg = h >= 10 ? '#0959AA' : h > 0 ? '#8DBDF0' : 'var(--jp-gray-100)';
    cells.push(
      <div key={day} title={`${day}: ${h ? `${fmt(h, 1)} h worn` : 'no data'}`}
        style={{ width: 16, height: 16, borderRadius: 3, background: bg }} />,
    );
  }
  return (
    <div className="ja-card">
      <div className="ja-card-header">
        <h4 className="ja-card-title">Coverage</h4>
        <span className="ja-card-meta">{startDate} to {endDate}</span>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>{cells}</div>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12, color: 'var(--jp-gray-600)', marginTop: 10 }}>
        <span><span aria-hidden="true" style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: '#0959AA', marginRight: 6 }} />valid day (10+ h worn)</span>
        <span><span aria-hidden="true" style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: '#8DBDF0', marginRight: 6 }} />partly worn</span>
        <span><span aria-hidden="true" style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: 'var(--jp-gray-100)', border: '1px solid var(--jp-gray-200)', marginRight: 6 }} />no data</span>
      </div>
    </div>
  );
}
CoverageCalendar.propTypes = { daily: PropTypes.array, startDate: PropTypes.string, endDate: PropTypes.string };

const last30 = () => ({ startDate: iso(Date.now() - 30 * DAY), endDate: iso(Date.now() - DAY) });

/** Keeps a range within the backend's limit, keeping the most recent days. */
const span = (r) => (Date.parse(r.endDate) - Date.parse(r.startDate)) / DAY + 1;
function clamp(r) {
  return span(r) > MAX_DAYS ? { startDate: iso(Date.parse(r.endDate) - (MAX_DAYS - 1) * DAY), endDate: r.endDate } : r;
}
const sameRange = (a, b) => a && b && a.startDate === b.startDate && a.endDate === b.endDate;

export default function HealthInsights({ projectId, participantIds, single, refreshKey }) {
  const [range, setRange] = useState(null); // what the date boxes show
  const [applied, setApplied] = useState(null); // what was last queried
  const [presets, setPresets] = useState(null); // {all, last} from completed fetches
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const idsKey = (participantIds || []).join(',');

  // Presets come from these participants' finished fetches that returned data, and update live
  // as fetches finish. Default: all fetched data, or the latest fetch when "all" spans more days
  // than one request allows. While the view is on a preset ("all" or "last"), it follows that
  // preset; typing dates or picking "Last 30 days" stops following. No fetches: last 30 days.
  const mode = useRef('auto'); // 'auto' | 'all' | 'last' | null (manual)
  const [finishedKey, setFinishedKey] = useState('');
  useEffect(() => {
    const ids = idsKey ? new Set(idsKey.split(',')) : null;
    mode.current = 'auto';
    const apply = (r) => { setRange(r); setApplied((prev) => (sameRange(prev, r) ? prev : r)); };
    return onSnapshot(
      query(collection(db, 'healthFetchJobs'), where('projectId', '==', projectId),
        orderBy('createdAt', 'desc'), limit(100)),
      (snap) => {
        const jobs = snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((j) => FINISHED.includes(j.status) &&
          (j.rowsWritten || 0) > 0 && j.startDate && j.endDate && (j.participantIds || []).length > 0 &&
          (!ids || (j.participantIds || []).some((p) => ids.has(p))));
        setFinishedKey(jobs.map((j) => `${j.id}:${j.rowsWritten}`).join(','));
        if (!jobs.length) {
          setPresets(null);
          if (mode.current) apply(last30());
          return;
        }
        const all = {
          startDate: jobs.reduce((m, j) => (j.startDate < m ? j.startDate : m), jobs[0].startDate),
          endDate: jobs.reduce((m, j) => (j.endDate > m ? j.endDate : m), jobs[0].endDate),
        };
        const p = { all: clamp(all), last: clamp({ startDate: jobs[0].startDate, endDate: jobs[0].endDate }) };
        setPresets(p);
        if (mode.current === 'auto') apply(span(all) > MAX_DAYS ? p.last : p.all);
        else if (mode.current) apply(p[mode.current]);
      },
      (err) => {
        console.error('healthFetchJobs query failed', err);
        if (mode.current) apply(last30());
      },
    );
  }, [projectId, idsKey]);

  const load = useCallback(async () => {
    if (!applied) return;
    setLoading(true);
    setError(null);
    try {
      const res = await httpsCallable(functions, 'getHealthInsights')({
        projectId, participantIds: idsKey ? idsKey.split(',') : [], ...applied,
      });
      setData(res.data);
    } catch (err) {
      setError(err.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [projectId, idsKey, applied]);

  // Reload when the range changes or a fetch for these participants finishes.
  useEffect(() => { load(); }, [load, refreshKey, finishedKey]);

  const show = (r, follow = null) => {
    mode.current = follow;
    setRange(r);
    setApplied(r);
  };
  const edited = range && applied && (range.startDate !== applied.startDate || range.endDate !== applied.endDate);

  const s = data?.stats;
  const hourly = data?.hourly || [];
  const daily = data?.daily || [];
  const noData = data && s?.participants === 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {range && (
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <label style={{ fontSize: 13 }}>From<br />
          <input type="date" className="ja-input" value={range.startDate}
            onChange={(e) => setRange({ ...range, startDate: e.target.value })} />
        </label>
        <label style={{ fontSize: 13 }}>To<br />
          <input type="date" className="ja-input" value={range.endDate}
            onChange={(e) => setRange({ ...range, endDate: e.target.value })} />
        </label>
        <button type="button" className="ja-btn ja-btn-primary" onClick={() => show(range)}
          disabled={loading || !edited || !range.startDate || !range.endDate}>
          Show
        </button>
        {presets && (
          <>
            <button type="button" className="ja-btn ja-btn-ghost" onClick={() => show(presets.all, 'all')} disabled={loading}>
              All fetched data
            </button>
            <button type="button" className="ja-btn ja-btn-ghost" onClick={() => show(presets.last, 'last')} disabled={loading}>
              Last fetch
            </button>
          </>
        )}
        <button type="button" className="ja-btn ja-btn-ghost" onClick={() => show(last30())} disabled={loading}>
          Last 30 days
        </button>
        <button type="button" className="ja-btn ja-btn-ghost" onClick={load} disabled={loading}>↻ Refresh</button>
        <span style={{ ...muted, fontSize: 12 }}>From jamasp_health in BigQuery. Steps and heart rate use valid days (10+ hours worn).</span>
      </div>
      )}

      {loading && <div className="ja-progress"><div className="ja-progress-bar" /></div>}
      {error && <p style={{ ...muted, color: '#C0392B' }}>Could not load insights: {error}</p>}
      {noData && <p style={muted}>No Google Health data in this range{single ? ' for this participant' : ' for the selected participants'}. Fetch it first, or pick other dates.</p>}

      {s && !noData && (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {!single && <Stat label="Participants" value={fmt(s.participants)} sub="with data in range" />}
            <Stat label="Valid days" value={fmt(s.validDays)} sub="10+ h worn" />
            <Stat label="Wear" value={`${fmt(s.wearHours, 1)} h`} sub="per day with data" />
            <Stat label="Steps" value={fmt(s.steps)} sub="per valid day" />
            <Stat label="Sleep" value={`${fmt(s.asleepHours, 1)} h`} sub="main sleep per night" />
            <Stat label="Resting HR" value={`${fmt(s.rhr, 1)} bpm`} />
            <Stat label="HRV" value={`${fmt(s.hrv, 1)} ms`} sub="daily" />
          </div>

          {single && <CoverageCalendar daily={daily} startDate={data.range.startDate} endDate={data.range.endDate} />}

          <ChartCard title="Daily steps" note={single ? null : 'Mean across participants with a valid day.'} height={200}>
            <BarChart data={daily} margin={{ top: 4, right: 12, bottom: 0, left: 0 }}>
              <CartesianGrid {...grid} />
              <XAxis dataKey="day" {...axis} minTickGap={24} />
              <YAxis {...axis} width={48} />
              <Tooltip formatter={(v) => fmt(v)} />
              <Bar dataKey="steps" fill={BLUE} name="Steps" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ChartCard>

          <ChartCard title="Sleep and resting heart rate" height={200}>
            <LineChart data={daily} margin={{ top: 4, right: 12, bottom: 0, left: 0 }}>
              <CartesianGrid {...grid} />
              <XAxis dataKey="day" {...axis} minTickGap={24} />
              <YAxis yAxisId="sleep" {...axis} width={44} tickFormatter={(v) => `${Math.round(v / 60)}h`} />
              <YAxis yAxisId="rhr" orientation="right" {...axis} width={40} domain={['auto', 'auto']} />
              <Tooltip formatter={(v, n) => (n === 'Asleep' ? `${fmt(v / 60, 1)} h` : `${fmt(v, 1)} bpm`)} />
              <Line yAxisId="sleep" dataKey="asleepMinutes" name="Asleep" stroke={BLUE} dot={false} strokeWidth={2} connectNulls={false} />
              <Line yAxisId="rhr" dataKey="rhr" name="Resting HR" stroke="#B45309" dot={false} strokeWidth={1.5} connectNulls={false} />
            </LineChart>
          </ChartCard>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16 }}>
            <ChartCard title="Heart rate by hour of day" height={200}>
              <LineChart data={hourly} margin={{ top: 4, right: 12, bottom: 0, left: 0 }}>
                <CartesianGrid {...grid} />
                <XAxis dataKey="hour" {...axis} tickFormatter={(h) => `${h}h`} />
                <YAxis {...axis} width={36} domain={['auto', 'auto']} />
                <Tooltip formatter={(v) => `${fmt(v, 1)} bpm`} labelFormatter={(h) => `${h}:00`} />
                <Line dataKey="hr" name="Heart rate" stroke={BLUE} dot={false} strokeWidth={2} />
              </LineChart>
            </ChartCard>
            <ChartCard title="Steps by hour of day" height={200}>
              <BarChart data={hourly} margin={{ top: 4, right: 12, bottom: 0, left: 0 }}>
                <CartesianGrid {...grid} />
                <XAxis dataKey="hour" {...axis} tickFormatter={(h) => `${h}h`} />
                <YAxis {...axis} width={44} />
                <Tooltip formatter={(v) => fmt(v)} labelFormatter={(h) => `${h}:00`} />
                <Bar dataKey="steps" name="Steps" fill={BLUE} radius={[2, 2, 0, 0]} />
              </BarChart>
            </ChartCard>
            <ChartCard title="Asleep by hour of day" note="Share of nights asleep at each hour (main sleep)." height={180}>
              <BarChart data={hourly} margin={{ top: 4, right: 12, bottom: 0, left: 0 }}>
                <CartesianGrid {...grid} />
                <XAxis dataKey="hour" {...axis} tickFormatter={(h) => `${h}h`} />
                <YAxis {...axis} width={40} domain={[0, 1]} tickFormatter={(v) => `${Math.round(v * 100)}%`} />
                <Tooltip formatter={(v) => `${Math.round(v * 100)}%`} labelFormatter={(h) => `${h}:00`} />
                <Bar dataKey="sleepShare" name="Nights asleep" fill="#02294F" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ChartCard>
          </div>

          {!single && data.participants.length > 0 && (
            <div className="ja-card">
              <div className="ja-card-header"><h4 className="ja-card-title">By participant</h4></div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr>
                      {['Participant', 'Days with data', 'Valid days', 'Wear h/day', 'Steps', 'Sleep h', 'Resting HR', 'HRV'].map((h) => (
                        <th key={h} style={{ textAlign: 'left', fontWeight: 500, fontSize: 12, color: 'var(--jp-gray-600)', padding: '6px 10px', borderBottom: '1px solid var(--jp-gray-200)', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.participants.map((p) => (
                      <tr key={p.participantId}>
                        {[
                          <a key="id" href={`/admin/participants/${p.participantId}/project/${projectId}`} style={{ fontFamily: 'var(--jp-mono)', fontSize: 12 }}>{p.participantId}</a>,
                          fmt(p.daysWithData), fmt(p.validDays), fmt(p.wearHours, 1), fmt(p.steps),
                          p.asleepMinutes == null ? '–' : fmt(p.asleepMinutes / 60, 1), fmt(p.rhr, 1), fmt(p.hrv, 1),
                        ].map((v, i) => (
                          // eslint-disable-next-line react/no-array-index-key
                          <td key={i} style={{ padding: '7px 10px', borderBottom: '1px solid var(--jp-gray-100)' }}>{v}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {data.dataTypes.length > 0 && (
            <details className="ja-card">
              <summary style={{ cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>Data types received ({data.dataTypes.length})</summary>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginTop: 10 }}>
                <tbody>
                  {data.dataTypes.map((t) => (
                    <tr key={t.dataType}>
                      <td style={{ padding: '5px 10px', fontFamily: 'var(--jp-mono)', fontSize: 12 }}>{t.dataType}</td>
                      <td style={{ padding: '5px 10px' }}>{fmt(t.records)} records</td>
                      {!single && <td style={{ padding: '5px 10px' }}>{fmt(t.participants)} participants</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          )}
        </>
      )}
    </div>
  );
}

HealthInsights.propTypes = {
  projectId: PropTypes.string.isRequired,
  participantIds: PropTypes.arrayOf(PropTypes.string),
  single: PropTypes.bool,
  refreshKey: PropTypes.any,
};
